/**
 * Customer Experience Scorecard
 * NPS/satisfaction trends correlated with staff, menu items, time-of-day.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Heart, Star, TrendingUp, TrendingDown, ChevronDown, MapPin, Clock, Users, MessageSquare, ThumbsUp, ThumbsDown, ArrowRight } from 'lucide-react';
import { getIndustryTerms } from '@/config/industryTerminology';

// Neutral, terminology-adapted CX data for every industry without a bespoke
// dataset — so a real client never sees hospitality reviews (drive-through,
// food quality, named QSR locations).
const buildGenericCX = (t) => ({
  overallNPS: 54,
  npsTrend: '+5',
  avgRating: 4.5,
  ratingTrend: '+0.2',
  totalReviews: 168,
  responseRate: '88%',
  sentimentBreakdown: { positive: 70, neutral: 21, negative: 9 },
  locationScores: [],
  timeOfDay: [],
  staffCorrelation: [
    { name: 'Top performer', avgRating: 4.8, reviewMentions: 16, sentiment: `Frequently praised by ${t.customersLower} for responsiveness and care` },
    { name: 'Rising performer', avgRating: 4.6, reviewMentions: 10, sentiment: 'Consistently positive feedback, fewer complaints during their shifts' },
  ],
  topThemes: [
    { theme: 'Communication', mentions: 96, sentiment: 'positive', trend: 'improving', detail: `Clear, timely communication is the top driver of ${t.customerLower} satisfaction. Up 18% in positive mentions.` },
    { theme: 'Responsiveness', mentions: 81, sentiment: 'mixed', trend: 'improving', detail: `${t.customers} reward fast follow-up. Response time improving but still the most common complaint when it slips.` },
    { theme: 'Quality of Service', mentions: 112, sentiment: 'positive', trend: 'stable', detail: 'Consistently the strongest positive theme — "professional" and "thorough" appear most often.' },
    { theme: 'Value for Money', mentions: 47, sentiment: 'mixed', trend: 'stable', detail: 'Most price concerns come from new ' + t.customersLower + '; existing relationships rarely raise it.' },
  ],
});

const QSR_CX_DATA = {
  overallNPS: 52,
  npsTrend: '+6',
  avgRating: 4.4,
  ratingTrend: '+0.2',
  totalReviews: 342,
  responseRate: '89%',
  sentimentBreakdown: { positive: 68, neutral: 22, negative: 10 },
  locationScores: [
    { location: 'Joondalup', state: 'WA', nps: 64, rating: 4.7, trend: '+8', topPraise: 'Order accuracy (99.2%)', topComplaint: 'Limited parking', reviewCount: 72 },
    { location: 'Southport', state: 'QLD', nps: 58, rating: 4.5, trend: '+4', topPraise: 'Fast service, friendly staff', topComplaint: 'Indoor seating cleanliness', reviewCount: 84 },
    { location: 'Penrith', state: 'NSW', nps: 54, rating: 4.4, trend: '+2', topPraise: 'Consistent food quality', topComplaint: 'Drive-through wait during lunch', reviewCount: 78 },
    { location: 'Footscray', state: 'VIC', nps: 42, rating: 4.2, trend: '-1', topPraise: 'Menu variety', topComplaint: 'Drive-through wait (4.9 min avg)', reviewCount: 62 },
    { location: 'Prospect', state: 'SA', nps: 38, rating: 4.0, trend: '-3', topPraise: 'Value for money', topComplaint: 'Slow service during peak hours', reviewCount: 46 },
  ],
  timeOfDay: [
    { period: 'Breakfast (6-10am)', rating: 4.6, volume: '18%', insight: 'Highest satisfaction — smaller crowds, fresh food, faster service' },
    { period: 'Lunch (11am-2pm)', rating: 4.2, volume: '42%', insight: 'Highest volume but lowest rating. Wait times drive most complaints.' },
    { period: 'Afternoon (2-5pm)', rating: 4.5, volume: '15%', insight: 'Low volume, consistent quality. Good snack/coffee attachment.' },
    { period: 'Dinner (5-8pm)', rating: 4.3, volume: '20%', insight: 'Family orders dominate. Accuracy is the #1 driver of satisfaction.' },
    { period: 'Late Night (8-close)', rating: 4.1, volume: '5%', insight: 'Lowest rating. Reduced staff and menu causes frustration.' },
  ],
  staffCorrelation: [
    { name: 'Sarah M.', avgRating: 4.8, reviewMentions: 14, sentiment: 'Frequently praised for friendliness and speed' },
    { name: 'Marcus C.', avgRating: 4.7, reviewMentions: 11, sentiment: 'Customers mention "efficient" and "helpful" consistently' },
    { name: 'Emma R.', avgRating: 4.6, reviewMentions: 8, sentiment: '15% fewer complaints during her shifts' },
  ],
  topThemes: [
    { theme: 'Wait Time', mentions: 89, sentiment: 'negative', trend: 'improving', detail: 'Most common complaint, but improving (-12% mentions vs last month). Footscray and Prospect are the main drivers.' },
    { theme: 'Food Quality', mentions: 124, sentiment: 'positive', trend: 'stable', detail: 'Consistently the top positive theme. "Always fresh" and "good value" are the most common phrases.' },
    { theme: 'Staff Friendliness', mentions: 98, sentiment: 'positive', trend: 'improving', detail: 'Up 18% in positive mentions. Southport and Joondalup lead. Correlates with the new training program.' },
    { theme: 'Order Accuracy', mentions: 56, sentiment: 'mixed', trend: 'improving', detail: 'Network avg: 97.8%. Joondalup at 99.2% (top). Footscray at 96.1% (needs attention). Each 1% improvement = ~$400/mo saved in remakes.' },
  ],
};

const TECH_CX_DATA = {
  overallNPS: 62,
  npsTrend: '+4',
  avgRating: 4.6,
  ratingTrend: '+0.1',
  totalReviews: 189,
  responseRate: '94%',
  sentimentBreakdown: { positive: 74, neutral: 18, negative: 8 },
  locationScores: [],
  timeOfDay: [],
  staffCorrelation: [
    { name: 'Priya S. (CS)', avgRating: 4.9, reviewMentions: 24, sentiment: 'Highest-rated CS rep. "Goes above and beyond" appears in 60% of her feedback.' },
    { name: 'Alex R. (AE)', avgRating: 4.7, reviewMentions: 18, sentiment: 'Clients praise his responsiveness and product knowledge.' },
  ],
  topThemes: [
    { theme: 'Onboarding Experience', mentions: 45, sentiment: 'positive', trend: 'improving', detail: 'New email sequence driving 3x engagement. Time-to-value reduced from 14 days to 7 days.' },
    { theme: 'API Documentation', mentions: 38, sentiment: 'mixed', trend: 'stable', detail: '65% positive, 35% request more examples. Enterprise customers especially need better webhook docs.' },
    { theme: 'Support Response Time', mentions: 32, sentiment: 'positive', trend: 'improving', detail: 'Avg first response: 2.4 hrs (down from 4.1 hrs). Enterprise SLA: 100% met this month.' },
    { theme: 'Pricing Clarity', mentions: 28, sentiment: 'negative', trend: 'stable', detail: 'Usage-based pricing confuses 22% of trial users. Consider a pricing calculator or clearer tier comparison.' },
  ],
};

const npsColor = (nps) => {
  if (nps >= 60) return 'text-emerald-700';
  if (nps >= 40) return 'text-blue-700';
  if (nps >= 20) return 'text-amber-700';
  return 'text-red-700';
};

const npsBg = (nps) => {
  if (nps >= 60) return 'bg-emerald-50 border-emerald-200';
  if (nps >= 40) return 'bg-blue-50 border-blue-200';
  if (nps >= 20) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
};

export const CustomerExperienceScorecard = ({ industry = 'general' }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const t = getIndustryTerms(industry);
  const data = t.key === 'technology' ? TECH_CX_DATA : t.key === 'hospitality' ? QSR_CX_DATA : buildGenericCX(t);
  const isQSR = t.key === 'hospitality';

  const toggle = (section) => setExpandedSection(expandedSection === section ? null : section);

  return (
    <Card className="glass-card overflow-hidden" data-testid="customer-experience-scorecard">
      <div className="h-1 bg-gradient-to-r from-pink-500 via-rose-400 to-red-400" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            {t.customer} Experience
          </CardTitle>
          <Badge variant="outline" className="text-[10px]">
            {data.totalReviews} reviews analysed
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">Satisfaction trends with staff, timing, and theme correlation</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Top-level Metrics — compact grid, not full-width rows */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className={`rounded-xl p-3 text-center border ${npsBg(data.overallNPS)}`}>
            <p className="text-[10px] uppercase text-slate-500 font-semibold">NPS</p>
            <p className={`text-2xl font-bold ${npsColor(data.overallNPS)}`}>{data.overallNPS}</p>
            <p className="text-[10px] text-emerald-600 font-semibold">{data.npsTrend} vs last period</p>
          </div>
          <div className="bg-white/80 rounded-xl p-3 text-center border border-slate-100">
            <p className="text-[10px] uppercase text-slate-500 font-semibold">Avg Rating</p>
            <div className="flex items-center justify-center gap-1">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="text-2xl font-bold text-slate-800">{data.avgRating}</span>
            </div>
            <p className="text-[10px] text-emerald-600 font-semibold">{data.ratingTrend}</p>
          </div>
          <div className="bg-white/80 rounded-xl p-3 text-center border border-slate-100">
            <p className="text-[10px] uppercase text-slate-500 font-semibold">Response Rate</p>
            <p className="text-2xl font-bold text-slate-800">{data.responseRate}</p>
            <p className="text-[10px] text-slate-400">to reviews</p>
          </div>
          <div className="bg-white/80 rounded-xl p-3 text-center border border-slate-100">
            <p className="text-[10px] uppercase text-slate-500 font-semibold">Sentiment</p>
            <div className="flex items-center justify-center gap-1 mt-1">
              <div className="h-2 rounded-full bg-emerald-400" style={{ width: `${data.sentimentBreakdown.positive * 0.6}px` }} />
              <div className="h-2 rounded-full bg-slate-300" style={{ width: `${data.sentimentBreakdown.neutral * 0.6}px` }} />
              <div className="h-2 rounded-full bg-red-400" style={{ width: `${data.sentimentBreakdown.negative * 0.6}px` }} />
            </div>
            <p className="text-[10px] text-slate-500 mt-1">{data.sentimentBreakdown.positive}% positive</p>
          </div>
        </div>

        {/* Location Scores (QSR) */}
        {isQSR && data.locationScores.length > 0 && (
          <div
            className="border border-pink-100 rounded-xl overflow-hidden cursor-pointer"
            onClick={() => toggle('locations')}
            data-testid="cx-locations-section"
          >
            <div className="flex items-center justify-between p-3 bg-pink-50/50">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-pink-500" />
                <span className="text-sm font-semibold">Location Scores</span>
                <Badge variant="outline" className="text-[10px]">{data.locationScores.length} venues</Badge>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedSection === 'locations' ? 'rotate-180' : ''}`} />
            </div>
            {expandedSection === 'locations' && (
              <div className="px-3 pb-3 space-y-1.5 border-t border-pink-100">
                {data.locationScores.map((loc, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/70 rounded-lg p-2.5 border border-slate-100">
                    <span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium">{loc.location}, {loc.state}</p>
                      <p className="text-[10px] text-slate-400">{loc.reviewCount} reviews</p>
                    </div>
                    <div className="text-center shrink-0">
                      <p className={`text-sm font-bold ${npsColor(loc.nps)}`}>{loc.nps}</p>
                      <p className="text-[10px] text-slate-400">NPS</p>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                      <span className="text-xs font-semibold">{loc.rating}</span>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${loc.trend.startsWith('+') ? 'text-emerald-600 border-emerald-200' : 'text-red-500 border-red-200'}`}>
                      {loc.trend}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Top Themes */}
        <div
          className="border border-pink-100 rounded-xl overflow-hidden cursor-pointer"
          onClick={() => toggle('themes')}
          data-testid="cx-themes-section"
        >
          <div className="flex items-center justify-between p-3 bg-pink-50/50">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-pink-500" />
              <span className="text-sm font-semibold">Review Themes</span>
              <Badge variant="outline" className="text-[10px]">{data.topThemes.length} themes</Badge>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedSection === 'themes' ? 'rotate-180' : ''}`} />
          </div>
          {expandedSection === 'themes' && (
            <div className="px-3 pb-3 space-y-2 border-t border-pink-100">
              {data.topThemes.map((theme, i) => (
                <div key={i} className="bg-white/70 rounded-lg p-3 border border-slate-100 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {theme.sentiment === 'positive' ? <ThumbsUp className="w-3.5 h-3.5 text-emerald-500" /> : theme.sentiment === 'negative' ? <ThumbsDown className="w-3.5 h-3.5 text-red-500" /> : <ArrowRight className="w-3.5 h-3.5 text-amber-500" />}
                      <span className="text-xs font-semibold">{theme.theme}</span>
                      <Badge variant="outline" className="text-[10px]">{theme.mentions} mentions</Badge>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${theme.trend === 'improving' ? 'text-emerald-600 border-emerald-200' : 'text-slate-500'}`}>
                      {theme.trend}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">{theme.detail}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Staff Correlation */}
        {data.staffCorrelation.length > 0 && (
          <div
            className="border border-pink-100 rounded-xl overflow-hidden cursor-pointer"
            onClick={() => toggle('staff')}
            data-testid="cx-staff-section"
          >
            <div className="flex items-center justify-between p-3 bg-pink-50/50">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-pink-500" />
                <span className="text-sm font-semibold">Staff Impact on CX</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedSection === 'staff' ? 'rotate-180' : ''}`} />
            </div>
            {expandedSection === 'staff' && (
              <div className="px-3 pb-3 space-y-1.5 border-t border-pink-100">
                {data.staffCorrelation.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/70 rounded-lg p-2.5 border border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-pink-50 border border-pink-200 flex items-center justify-center text-xs font-bold text-pink-700">
                      {s.avgRating}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium">{s.name}</p>
                      <p className="text-[10px] text-slate-500">{s.sentiment}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{s.reviewMentions} mentions</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Time of Day (QSR) */}
        {isQSR && data.timeOfDay.length > 0 && (
          <div
            className="border border-pink-100 rounded-xl overflow-hidden cursor-pointer"
            onClick={() => toggle('timeOfDay')}
            data-testid="cx-timeofday-section"
          >
            <div className="flex items-center justify-between p-3 bg-pink-50/50">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-pink-500" />
                <span className="text-sm font-semibold">Satisfaction by Time of Day</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedSection === 'timeOfDay' ? 'rotate-180' : ''}`} />
            </div>
            {expandedSection === 'timeOfDay' && (
              <div className="px-3 pb-3 space-y-1.5 border-t border-pink-100">
                {data.timeOfDay.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/70 rounded-lg p-2.5 border border-slate-100">
                    <div className="w-10 text-center shrink-0">
                      <div className="flex items-center justify-center gap-0.5">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        <span className="text-sm font-bold">{t.rating}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium">{t.period}</p>
                      <p className="text-[10px] text-slate-500">{t.insight}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] shrink-0">{t.volume} of traffic</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerExperienceScorecard;
